import type { Transaction } from '../../features/queries/meGetGamificationAccountTransactions';
import { __, formatDate, formatDateTime } from '../../utils';
import { ListItem, ListItemText, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  transaction: Transaction;
  onOpen: () => void;
};

const TransactionsListItem: React.FC<Props> = ({ transaction, onOpen }) => {
  const statusMap: Record<string, { label: string; color: string }> = {
    reserved: {
      label: __('Rezervované'),
      color: 'warning',
    },
    confirmed: {
      label: __('Potvrdené'),
      color: 'success',
    },
    expired: {
      label: __('Expirované'),
      color: 'rgba(0,0,0,0.32)', // info.light
    },
    cancelled: {
      label: __('Zrušené'),
      color: 'error',
    },
  };

  const transactionStatus = statusMap[transaction.status] || {
    label: transaction.status,
    icon: transaction.status,
    color: 'textSecondary',
  };

  const getPointsColor = () => {
    if (transaction.status === 'confirmed') {
      const points = transaction.points || 0;
      return points >= 0 ? 'success.main' : 'error.main';
    }
    return transactionStatus.color;
  };

  return (
    <ListItem
      onClick={onOpen}
      sx={{
        flexDirection: 'row',
        alignItems: transaction.status === 'confirmed' ? 'start' : 'start',
        flex: 1,
        px: 2,
        py: 1,
        '& .MuiSvgIcon-root': {
          color: transactionStatus.color,
        },
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: (theme) => theme.palette.action.hover,
        },
      }}
    >
      <ListItemText
        sx={{ my: 0 }}
        primary={
          <Typography
            variant='body1'
            lineHeight={1.3}
            color={
              transaction.status === 'expired'
                ? 'rgba(0,0,0,0.32)'
                : 'textPrimary'
            }
            sx={{
              fontWeight: '700 !important',
              textDecoration:
                transaction.status === 'expired' ? 'line-through' : 'none',
            }}
          >
            {transaction.description}
          </Typography>
        }
        secondary={
          transaction.created_at && (
            <Typography
              variant='caption'
              sx={{
                textDecoration: 'italic',
                color: (theme) => theme.palette.text.secondary,
              }}
            >
              {formatDate(new Date(transaction.created_at))}
            </Typography>
          )
        }
      />
      <Stack alignItems={'flex-end'}>
        <Stack direction={'row'} spacing={0.5} alignItems='center'>
          <Typography
            fontWeight='bold'
            variant='body1'
            color={getPointsColor()}
            sx={{
              whiteSpace: 'nowrap',
              fontWeight: '700 !important',
              textDecoration:
                transaction.status === 'expired' ? 'line-through' : 'none',
            }}
          >
            {transaction.status === 'confirmed' ||
            transaction.status === 'reserved'
              ? `${transaction.points > 0 ? '+' : ''}${transaction.points}`
              : `${transaction.original_points || 0}`}{' '}
            {__('b')}
          </Typography>
        </Stack>
        {transaction.expires_at && (
          <Typography
            variant='caption'
            sx={{
              textDecoration: 'italic',
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            {transaction.status === 'expired'
              ? __('Expirované')
              : transaction.status === 'cancelled'
                ? __('Zrušené')
                : transaction.status === 'reserved'
                  ? __('Rezervované do')
                  : null}{' '}
            {transaction.status !== 'confirmed' &&
              formatDateTime(new Date(transaction.expires_at))}
          </Typography>
        )}
      </Stack>
    </ListItem>
  );
};

export default TransactionsListItem;
